import React, { useState, useEffect, useRef, useCallback } from "react";
import Skeleton from "../common/skeleton";
import EmptyState from "../common/empty_state";
import { formatKst } from "../../utils/time";

const RANGE_OPTIONS = [
    { key: "today", label: "오늘" },
    { key: "7d", label: "7일" },
    { key: "30d", label: "30일" },
    { key: "all", label: "전체" },
];

const KIND_OPTIONS = [
    { key: "all", label: "전체" },
    { key: "new_listing", label: "신규상장만" },
    { key: "status_change", label: "상태변경만" },
];

function WalletChangeLog({
    changes = [],
    loading = false,
    pagination = { count: 0, next: null, previous: null },
    onPageChange,
    filterCurrency = "",
    onFilterCurrencyChange,
    error = false,
    range = "7d",
    onRangeChange = null,
    kind = "all",
    onKindChange = null,
}) {
    const [localCurrency, setLocalCurrency] = useState(filterCurrency);
    const debounceRef = useRef(null);

    useEffect(() => {
        setLocalCurrency(filterCurrency);
    }, [filterCurrency]);

    const handleCurrencyInput = useCallback((e) => {
        const value = e.target.value;
        setLocalCurrency(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onFilterCurrencyChange(value);
        }, 400);
    }, [onFilterCurrencyChange]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);
    /* -- 필드명 한글 변환 -- */
    const fieldLabel = (field) => {
        if (field === "deposit_status") return "입금";
        if (field === "withdrawal_status") return "출금";
        return field;
    };

    /* -- 상태 배지 렌더링 -- */
    const renderStatusBadge = (status) => {
        // 신규 상장 시그널: EN detector 가 old_status="" 로 기록 (null 도 방어적 처리)
        if (status === "" || status == null) {
            return (
                <span className="inline-flex items-center rounded-full bg-brand-subtle px-2 py-0.5 text-xs font-medium text-brand-light">
                    신규
                </span>
            );
        }
        if (status === "normal") {
            return (
                <span className="inline-flex items-center rounded-full bg-gain-subtle px-2 py-0.5 text-xs font-medium text-gain">
                    정상
                </span>
            );
        }
        return (
            <span className="inline-flex items-center rounded-full bg-loss-subtle px-2 py-0.5 text-xs font-medium text-loss">
                정지
            </span>
        );
    };

    /* -- 초기 로딩 (데이터 없을 때만 스켈레톤) -- */
    const isInitialLoad = loading && changes.length === 0;
    if (isInitialLoad) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="flex flex-col gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} variant="rect" height="40px" />
                    ))}
                </div>
            </div>
        );
    }

    /* -- 에러 상태 -- */
    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="rounded-lg border border-loss/20 bg-loss/5 p-4 text-center text-sm text-loss">
                    데이터를 불러올 수 없습니다.
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
            {/* 필터 툴바 */}
            <div className="mb-4 rounded-xl border border-surface-border bg-surface-elevated p-3 sm:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-2">
                    {/* 기간 선택 */}
                    {onRangeChange && (
                        <div className="flex items-center gap-2">
                            <span className="shrink-0 text-xs font-medium text-content-muted">기간</span>
                            <div className="flex gap-1">
                                {RANGE_OPTIONS.map((opt) => {
                                    const active = opt.key === range;
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => onRangeChange(opt.key)}
                                            className={
                                                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors " +
                                                (active
                                                    ? "bg-brand/20 text-brand-light border border-brand/30 shadow-sm"
                                                    : "text-content-secondary hover:bg-surface-hover")
                                            }
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 구분 토글 */}
                    {onKindChange && (
                        <div className="flex items-center gap-2">
                            <span className="shrink-0 text-xs font-medium text-content-muted">구분</span>
                            <div className="flex gap-1">
                                {KIND_OPTIONS.map((opt) => {
                                    const active = opt.key === kind;
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => onKindChange(opt.key)}
                                            className={
                                                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors " +
                                                (active
                                                    ? "bg-brand/20 text-brand-light border border-brand/30 shadow-sm"
                                                    : "text-content-secondary hover:bg-surface-hover")
                                            }
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 코인 필터 input (debounced) */}
                    <div className="flex items-center gap-2 sm:ml-auto">
                        <span className="shrink-0 text-xs font-medium text-content-muted sm:hidden">코인</span>
                        <div className="relative flex-1 sm:flex-none">
                            <svg aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                placeholder="코인 검색..."
                                aria-label="코인 검색"
                                value={localCurrency}
                                onChange={handleCurrencyInput}
                                className="w-full sm:w-40 rounded-lg border border-surface-border bg-surface-card pl-8 pr-3 py-1.5 text-xs text-content placeholder:text-content-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 또는 빈 상태 */}
            <div className="relative">
            {loading && changes.length > 0 && (
                <div className="absolute inset-0 z-10 flex items-start justify-center rounded-lg bg-surface-card/60 pt-8 backdrop-blur-[1px]">
                    <svg className="h-5 w-5 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                </div>
            )}
            {changes.length === 0 && !loading ? (
                <EmptyState title="변경 이력 없음" description="조건에 맞는 변경 이력이 없습니다." />
            ) : (
                <>
                    {/* 모바일 카드 레이아웃 */}
                    <div className="sm:hidden space-y-2 max-h-96 overflow-y-auto pr-0.5">
                        {changes.map((item) => (
                            <div
                                key={item.id}
                                className="rounded-lg border border-surface-border bg-surface-elevated p-3"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-content">{item.currency}</span>
                                        <span className="text-xs text-content-secondary">{item.net_type || "-"}</span>
                                    </div>
                                    <span className="text-xs text-content-muted whitespace-nowrap">
                                        {item.detected_at ? formatKst(item.detected_at) : "-"}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs text-content-muted">{fieldLabel(item.field)}</span>
                                    <div className="flex items-center gap-1.5">
                                        {renderStatusBadge(item.old_status)}
                                        <span className="text-xs text-content-muted">→</span>
                                        {renderStatusBadge(item.new_status)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 데스크톱 테이블 레이아웃 */}
                    <div className="hidden sm:block overflow-x-auto max-h-[30rem] overflow-y-auto rounded-lg border border-surface-border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="sticky top-0 z-10 bg-surface-card text-left text-xs font-semibold text-content-muted uppercase tracking-wider">
                                    <th className="px-3 py-2">시간</th>
                                    <th className="px-3 py-2">코인</th>
                                    <th className="px-3 py-2">네트워크</th>
                                    <th className="px-3 py-2">변경 필드</th>
                                    <th className="px-3 py-2">이전</th>
                                    <th className="px-3 py-2">이후</th>
                                </tr>
                            </thead>
                            <tbody>
                                {changes.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="border-t border-surface-border hover:bg-surface-hover/50 transition-colors"
                                    >
                                        <td className="px-3 py-2.5 text-content-secondary text-xs whitespace-nowrap">
                                            {item.detected_at
                                                ? formatKst(item.detected_at)
                                                : "-"}
                                        </td>
                                        <td className="px-3 py-2.5 font-medium text-content">
                                            {item.currency}
                                        </td>
                                        <td className="px-3 py-2.5 text-content-secondary">
                                            {item.net_type || "-"}
                                        </td>
                                        <td className="px-3 py-2.5 text-content-secondary">
                                            {fieldLabel(item.field)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {renderStatusBadge(item.old_status)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {renderStatusBadge(item.new_status)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            </div>

            {/* 페이지네이션 */}
            <div className="mt-3 flex items-center justify-between">
                <button
                    type="button"
                    disabled={!pagination.previous}
                    onClick={() => onPageChange("prev")}
                    className="rounded-lg border border-surface-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    이전
                </button>
                <span className="text-xs text-content-muted">
                    총 {pagination.count}건
                </span>
                <button
                    type="button"
                    disabled={!pagination.next}
                    onClick={() => onPageChange("next")}
                    className="rounded-lg border border-surface-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    다음
                </button>
            </div>
        </div>
    );
}

export default WalletChangeLog;
